import { useCallback } from 'react';
import {
  generateStableKey,
  parseMultipleItems,
  smartProcessItem,
  addToHistory,
  addPendingChange,
  queueChangeOffline,
  getItemSortKey
} from '../utils/helpers';
import { postJson, putJson, del as apiDelete } from '../utils/apiClient';

/**
 * Hook for item CRUD operations
 */
export default function useItemOperations({ cache, setCache, showNotification, setHighlightedItems, isOnline }) {

  // Create new item(s)
  const handleCreateItem = useCallback(async (listId, content) => {
    console.log(`🔍 [Create] handleCreateItem called with listId: "${listId}", content: "${content}"`);

    // Don't allow item creation for temporary lists
    if (listId.startsWith('temp-')) {
      console.log(`⚠️ [Create] Cannot create items for temporary list: ${listId}`);
      showNotification('Please wait for the list to be created before adding items.', 'warning');
      return;
    }

    const now = Date.now();
    const rawItems = parseMultipleItems(content);

    if (rawItems.length === 0) return;

    // Check for duplicates
    const currentItems = cache.items[listId] || [];
    const duplicates = [];
    const itemsToCreate = [];

    rawItems.forEach(itemContent => {
      const normalizedContent = itemContent.toLowerCase().trim();
      const existingItem = currentItems.find(item =>
        item.content.toLowerCase().trim() === normalizedContent
      );

      if (existingItem) {
        duplicates.push({
          content: itemContent,
          existingId: existingItem.id,
          wasCompleted: !!existingItem.is_completed,
        });
      } else {
        itemsToCreate.push(itemContent);
      }
    });

    // Re-adding a duplicate highlights the existing item. If the duplicate was
    // checked off, also un-check it — the user is signaling they want it back.
    if (duplicates.length > 0) {
      const wasCompletedIds = duplicates.filter(d => d.wasCompleted).map(d => d.existingId);

      // Highlight all duplicates briefly
      duplicates.forEach(dup => {
        setHighlightedItems(prev => new Set(prev).add(dup.existingId));
      });
      setTimeout(() => {
        setHighlightedItems(prev => {
          const newSet = new Set(prev);
          duplicates.forEach(dup => newSet.delete(dup.existingId));
          return newSet;
        });
      }, 2000);

      // Un-complete any duplicates that were already checked off
      if (wasCompletedIds.length > 0) {
        setCache(c => ({
          ...c,
          items: {
            ...c.items,
            [listId]: (c.items[listId] || []).map(i =>
              wasCompletedIds.includes(i.id) ? { ...i, is_completed: false } : i
            )
          }
        }));

        if (isOnline) {
          for (const id of wasCompletedIds) {
            putJson(`/lists/${listId}/items/${id}`, { is_completed: false })
              .catch(err => console.error('Re-open duplicate failed', err));
          }
        } else {
          for (const id of wasCompletedIds) {
            queueChangeOffline(setCache, {
              type: 'updateItem',
              listId,
              payload: { itemId: id, updates: { is_completed: false } },
            });
          }
        }
      }
    }

    if (itemsToCreate.length === 0) return;

    // Process each item
    const processedItems = itemsToCreate.map((itemContent, index) => {
      const tempId = `temp-item-${now}-${index}`;
      const processed = smartProcessItem(itemContent, cache.items[listId] || [], cache.propertyCache || {});
      return { tempId, originalContent: itemContent, processed };
    });

    // Calculate position
    const maxPosition = currentItems.length > 0
      ? Math.max(...currentItems.map(getItemSortKey))
      : 0;

    // Create temp items
    const tempItems = processedItems.map((item, index) => ({
      id: item.tempId,
      tempId: item.tempId,
      content: item.processed.content,
      is_completed: false,
      created_at: now + index,
      position: Math.max(now + index, maxPosition + 1 + index),
      stableKey: generateStableKey({ id: item.tempId, tempId: item.tempId }),
      properties: item.processed.properties || {}
    }));

    // Optimistic update
    setCache(c => {
      const updatedCache = addToHistory(c, content);
      return {
        ...updatedCache,
        items: {
          ...updatedCache.items,
          [listId]: [...tempItems, ...(updatedCache.items[listId] || [])]
        }
      };
    });

    // If offline, skip API call entirely and queue immediately
    if (!isOnline) {
      setCache(c => {
        // Mark temp items as pending sync
        const updatedItems = (c.items[listId] || []).map(i => {
          if (processedItems.some(pi => pi.tempId === i.id)) {
            return { ...i, isPendingSync: true };
          }
          return i;
        });
        let updated = { ...c, items: { ...c.items, [listId]: updatedItems } };
        // Queue each item for sync
        processedItems.forEach(item => {
          updated = addPendingChange(updated, {
            type: 'createItem',
            listId,
            payload: { content: item.originalContent }
          });
        });
        return updated;
      });
      showNotification('Items saved offline - will sync when connected', 'info');
      return;
    }

    try {
      const { data: response } = await postJson(`/lists/${listId}/items/batch`, {
        items: processedItems.map(item => ({ content: item.originalContent }))
      });

      if (response.warnings && response.warnings.length > 0) {
        showNotification(response.warnings[0], 'warning');
      }

      // Remove temp items - Firebase listener will add real items
      setCache(c => ({
        ...c,
        items: {
          ...c.items,
          [listId]: c.items[listId].filter(i => !processedItems.some(pi => pi.tempId === i.id))
        }
      }));
    } catch (error) {
      console.error('Create items failed', error);

      // Fallback to individual creation if batch endpoint doesn't exist
      if (error.response?.status === 404) {
        for (const item of processedItems) {
          try {
            await postJson(`/lists/${listId}/items`, { content: item.originalContent });
          } catch (individualError) {
            console.error(`Failed to create item "${item.originalContent}":`, individualError);
          }
        }
        return;
      }

      // Network error: keep items visible, mark as pending sync, and queue
      if (!error.response) {
        setCache(c => {
          const updatedItems = (c.items[listId] || []).map(i => {
            if (processedItems.some(pi => pi.tempId === i.id)) {
              return { ...i, isPendingSync: true };
            }
            return i;
          });
          let updated = { ...c, items: { ...c.items, [listId]: updatedItems } };
          processedItems.forEach(item => {
            updated = addPendingChange(updated, {
              type: 'createItem',
              listId,
              payload: { content: item.originalContent }
            });
          });
          return updated;
        });
        showNotification('Items saved offline - will sync when connected', 'info');
      } else {
        // Server error: revert optimistic update
        setCache(c => ({
          ...c,
          items: {
            ...c.items,
            [listId]: c.items[listId].filter(i => !processedItems.some(pi => pi.tempId === i.id))
          },
          history: c.history[0]?.content === content ? c.history.slice(1) : c.history
        }));
      }
    }
  }, [cache, setCache, showNotification, setHighlightedItems, isOnline]);

  // Update an item
  const handleUpdateItem = useCallback(async (listId, itemId, updates) => {
    setCache(c => ({
      ...c,
      items: {
        ...c.items,
        [listId]: c.items[listId].map(i => i.id === itemId ? { ...i, ...updates } : i)
      }
    }));

    if (!isOnline) {
      queueChangeOffline(setCache, { type: 'updateItem', listId, payload: { itemId, updates } });
      return;
    }

    try {
      await putJson(`/lists/${listId}/items/${itemId}`, updates);
    } catch (error) {
      console.error('Update item failed', error);
      if (!error.response) {
        // Network error: queue for retry
        queueChangeOffline(setCache, { type: 'updateItem', listId, payload: { itemId, updates } });
      }
    }
  }, [setCache, isOnline]);

  // Rename item and reprocess
  const handleRenameItem = useCallback(async (listId, item, newContent) => {
    setCache(c => ({
      ...c,
      items: {
        ...c.items,
        [listId]: (c.items[listId] || []).map(i => i.id === item.id ? { ...i, content: newContent } : i)
      }
    }));

    // Without queueing, the optimistic rename above lives only in local state and
    // the next server snapshot silently restores the old name.
    if (!isOnline) {
      queueChangeOffline(setCache, { type: 'renameItem', listId, payload: { itemId: item.id, content: newContent } });
      return;
    }

    try {
      await putJson(`/lists/${listId}/items/${item.id}`, { content: newContent, properties: {} });
      await postJson(`/lists/${listId}/items/${item.id}/reprocess`, {});
    } catch (error) {
      console.error('Rename item failed', error);
      if (!error.response) {
        queueChangeOffline(setCache, { type: 'renameItem', listId, payload: { itemId: item.id, content: newContent } });
      }
    }
  }, [setCache, isOnline]);

  // Delete an item
  const handleDeleteItem = useCallback(async (listId, itemId) => {
    const itemToDelete = cache.items[listId]?.find(i => i.id === itemId);
    if (!itemToDelete) {
      console.warn('Item not found for deletion:', itemId);
      return;
    }

    const originalItems = cache.items[listId];
    const originalHistory = cache.history;

    // Optimistic update
    setCache(c => {
      const updatedCache = addToHistory(c, itemToDelete.content);
      return {
        ...updatedCache,
        items: {
          ...updatedCache.items,
          [listId]: updatedCache.items[listId].filter(i => i.id !== itemId)
        }
      };
    });

    if (!isOnline) {
      queueChangeOffline(setCache, { type: 'deleteItem', listId, payload: { itemId } });
      return;
    }

    try {
      await apiDelete(`/lists/${listId}/items/${itemId}`);
    } catch (error) {
      if (error?.response?.status === 404) return;
      console.error('Delete item failed', error);
      if (!error.response) {
        // Network error: keep item deleted, queue for retry
        queueChangeOffline(setCache, { type: 'deleteItem', listId, payload: { itemId } });
      } else {
        // Server error: revert
        setCache((c) => ({
          ...c,
          items: { ...c.items, [listId]: originalItems },
          history: originalHistory
        }));
      }
    }
  }, [cache, setCache, isOnline]);

  // AI sort items
  const handleAiSort = useCallback(async (listId, provider, sortType = 'smart') => {
    if (!isOnline) {
      showNotification('AI sorting requires an internet connection', 'warning');
      return;
    }
    try {
      const sortPrompts = {
        'department': 'Sort these shopping items by store department (produce, dairy, meat, pantry, frozen, etc.)',
        'category': 'Sort these shopping items by category (fruits, vegetables, proteins, grains, beverages, etc.)',
        'priority': 'Sort these shopping items by priority (essentials first, then nice-to-haves)',
        'smart': 'Sort these shopping items in the most logical order for shopping efficiency'
      };

      const { data: sortedItems } = await postJson(`/lists/${listId}/sort`, {
        provider,
        prompt: sortPrompts[sortType] || sortPrompts['smart']
      });

      // Merge rather than replace: anything the server response doesn't mention
      // (an item still pending sync, or one created since the request went out)
      // must stay visible instead of appearing to vanish.
      setCache(c => {
        const current = c.items[listId] || [];
        const currentById = new Map(current.map(i => [i.id, i]));
        const sortedIds = new Set(sortedItems.map(i => i.id));

        const reordered = sortedItems.map(item => {
          const existing = currentById.get(item.id);
          return {
            ...existing,
            ...item,
            stableKey: existing?.stableKey || generateStableKey(item)
          };
        });
        const untouched = current.filter(i => !sortedIds.has(i.id));

        return {
          ...c,
          items: { ...c.items, [listId]: [...reordered, ...untouched] }
        };
      });
    } catch (error) {
      console.error("AI Sort failed", error);
      // The server can refuse for reasons the user can act on (AI disabled for
      // the account, monthly quota spent) — don't fail silently.
      const message = error.response?.data?.message;
      showNotification(message || 'AI sorting failed. Please try again.', 'error');
    }
  }, [setCache, isOnline, showNotification]);

  // Add properties to an item
  const handleAddProperties = useCallback(async (listId, itemId, properties) => {
    try {
      const { data: updatedItem } = await postJson(`/lists/${listId}/items/${itemId}/properties`, {
        action: 'add_properties',
        properties
      });

      setCache(c => ({
        ...c,
        items: {
          ...c.items,
          [listId]: c.items[listId].map(i =>
            i.id === itemId ? { ...updatedItem, stableKey: i.stableKey } : i
          )
        }
      }));
    } catch (error) {
      console.error('Add properties failed', error);
    }
  }, [setCache]);

  // Translate items
  const handleTranslateItems = useCallback(async (listId, language) => {
    if (!isOnline) {
      showNotification('Translation requires an internet connection', 'warning');
      return;
    }
    try {
      const currentItems = cache.items[listId] || [];
      const itemsNeedingTranslation = currentItems.filter(item => {
        if (!item.properties) return true;
        return !item.properties[`name_${language}`];
      });

      if (itemsNeedingTranslation.length === 0) return;

      for (const item of itemsNeedingTranslation) {
        try {
          await postJson(`/lists/${listId}/items/${item.id}/properties`, {
            action: 'translate',
            language
          });
        } catch (error) {
          console.error(`Translation failed for item ${item.id}:`, error);
        }
      }
    } catch (error) {
      console.error('Batch translation failed', error);
    }
  }, [cache, isOnline, showNotification]);

  // Delete from history. History entries are plain strings, so compare directly.
  const handleDeleteFromHistory = useCallback((suggestion) => {
    setCache(c => ({
      ...c,
      history: (c.history || []).filter(item => item !== suggestion)
    }));
  }, [setCache]);

  // Clear all completed items from a list. Reuses handleDeleteItem per item so
  // each gets the same optimistic-remove, history-add, offline-queue and
  // 404/server-error handling. Deletes run in parallel.
  const handleClearCompleted = useCallback(async (listId) => {
    const completed = (cache.items[listId] || []).filter(i => i.is_completed);
    if (completed.length === 0) return;
    await Promise.all(completed.map(item => handleDeleteItem(listId, item.id)));
  }, [cache, handleDeleteItem]);

  // Expand suggested_subitems on an item into a populated subitems array.
  const handleExpandSubitems = useCallback(async (listId, item) => {
    if (!item || !Array.isArray(item.suggested_subitems) || item.suggested_subitems.length === 0) return;

    // Optimistic: build subitems locally so UI shows them immediately. Ids are
    // derived from the suggestion index and match what the server assigns, so a
    // sub-item toggled while offline still resolves after the expand syncs.
    const optimisticSubitems = item.suggested_subitems.map((content, i) => ({
      id: `sub-${i}`,
      content,
      is_completed: false,
      properties: {},
    }));

    setCache(c => ({
      ...c,
      items: {
        ...c.items,
        [listId]: (c.items[listId] || []).map(i =>
          i.id === item.id ? { ...i, subitems: optimisticSubitems } : i
        )
      }
    }));

    if (!isOnline) {
      queueChangeOffline(setCache, { type: 'expandSubitems', listId, payload: { itemId: item.id } });
      return;
    }

    try {
      const { data: updated } = await postJson(
        `/lists/${listId}/items/${item.id}/expand-subitems`,
        {}
      );
      // Reconcile with server-assigned subitem ids (Firebase listener will also catch up)
      setCache(c => ({
        ...c,
        items: {
          ...c.items,
          [listId]: (c.items[listId] || []).map(i =>
            i.id === item.id ? { ...i, subitems: updated.subitems || optimisticSubitems } : i
          )
        }
      }));
    } catch (error) {
      console.error('Expand subitems failed', error);
      if (!error.response) {
        // Network error: keep the optimistic sub-items and retry on reconnect.
        queueChangeOffline(setCache, { type: 'expandSubitems', listId, payload: { itemId: item.id } });
        return;
      }
      // Revert
      setCache(c => ({
        ...c,
        items: {
          ...c.items,
          [listId]: (c.items[listId] || []).map(i =>
            i.id === item.id ? { ...i, subitems: undefined } : i
          )
        }
      }));
    }
  }, [setCache, isOnline]);

  // Collapse subitems (clear them locally and on server). Keeps suggested_subitems intact.
  const handleCollapseSubitems = useCallback(async (listId, item) => {
    setCache(c => ({
      ...c,
      items: {
        ...c.items,
        [listId]: (c.items[listId] || []).map(i =>
          i.id === item.id ? { ...i, subitems: undefined } : i
        )
      }
    }));
    if (!isOnline) {
      queueChangeOffline(setCache, {
        type: 'updateItem',
        listId,
        payload: { itemId: item.id, updates: { subitems: null } },
      });
      return;
    }
    try {
      await putJson(`/lists/${listId}/items/${item.id}`, { subitems: null });
    } catch (error) {
      console.error('Collapse subitems failed', error);
      if (!error.response) {
        queueChangeOffline(setCache, {
          type: 'updateItem',
          listId,
          payload: { itemId: item.id, updates: { subitems: null } },
        });
      }
    }
  }, [setCache, isOnline]);

  // Toggle a single sub-item's completion state.
  const handleToggleSubitem = useCallback(async (listId, item, subitemId) => {
    const subitems = Array.isArray(item.subitems) ? item.subitems : [];
    const sub = subitems.find(s => s.id === subitemId);
    if (!sub) return;
    const nextCompleted = !sub.is_completed;
    const nextSubitems = subitems.map(s => s.id === subitemId ? { ...s, is_completed: nextCompleted } : s);

    // Mirror sub-item state onto parent: parent is complete iff all sub-items are.
    const parentShouldBeComplete = nextSubitems.length > 0 && nextSubitems.every(s => s.is_completed);
    const parentChanged = item.is_completed !== parentShouldBeComplete;

    // Optimistic
    setCache(c => ({
      ...c,
      items: {
        ...c.items,
        [listId]: (c.items[listId] || []).map(i =>
          i.id === item.id
            ? { ...i, subitems: nextSubitems, ...(parentChanged ? { is_completed: parentShouldBeComplete } : {}) }
            : i
        )
      }
    }));

    const queueToggle = () => {
      queueChangeOffline(setCache, {
        type: 'toggleSubitem',
        listId,
        payload: {
          itemId: item.id,
          subitemId,
          is_completed: nextCompleted,
          parentCompleted: parentChanged ? parentShouldBeComplete : null,
        },
      });
    };

    if (!isOnline) {
      queueToggle();
      return;
    }

    try {
      await putJson(
        `/lists/${listId}/items/${item.id}/subitems/${subitemId}`,
        { updates: { is_completed: nextCompleted } }
      );
      if (parentChanged) {
        await putJson(
          `/lists/${listId}/items/${item.id}`,
          { is_completed: parentShouldBeComplete }
        );
      }
    } catch (error) {
      console.error('Toggle subitem failed', error);
      if (!error.response) queueToggle();
    }
  }, [setCache, isOnline]);

  return {
    handleCreateItem,
    handleUpdateItem,
    handleRenameItem,
    handleDeleteItem,
    handleClearCompleted,
    handleAiSort,
    handleAddProperties,
    handleTranslateItems,
    handleDeleteFromHistory,
    handleExpandSubitems,
    handleCollapseSubitems,
    handleToggleSubitem,
  };
}
